/**
 * useAlertNotifications — plays a Bloomberg-style "ping" sound when a new
 * Drishti alert arrives, and requests/manages browser push notification
 * permission on first call.
 *
 * Usage:
 *   const { permissionGranted, requestPermission } = useAlertNotifications(prevAlertCount, newAlertCount);
 */
import { useEffect, useRef, useCallback, useState } from "react";

// ── Synthesise a brief "blip" using the Web Audio API (no file needed) ──────
function playAlertBlip() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();

    // Main tone – fast attack, short sustain
    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    osc.connect(gainNode);
    gainNode.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.12);
    gainNode.gain.setValueAtTime(0.001, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.32);

    // Second softer "follow-through" note
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(1100, ctx.currentTime + 0.18);
    osc2.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.32);
    gain2.gain.setValueAtTime(0.001, ctx.currentTime + 0.18);
    gain2.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.22);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.42);
    osc2.start(ctx.currentTime + 0.18);
    osc2.stop(ctx.currentTime + 0.44);
  } catch {
    // Audio API not available — silently skip
  }
}

// ── Show a browser push notification ─────────────────────────────────────────
function showPushNotification(title = "🚨 New Drishti Alert", body = "A new trading alert has been posted.") {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  try {
    new Notification(title, {
      body,
      icon: "/jamadhan-logo.png",
      badge: "/jamadhan-logo.png",
      tag: "drishti-alert",         // replaces the previous notification instead of stacking
      renotify: true,
    });
  } catch {
    // Firefox private mode or insecure context — skip
  }
}

// ── Main hook ─────────────────────────────────────────────────────────────────
export function useAlertNotifications(alertCount) {
  const prevCountRef = useRef(null);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );

  // On mount — ask for permission once if not yet decided
  useEffect(() => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      Notification.requestPermission().then((result) => {
        setPermission(result);
      });
    }
  }, []);

  // Watch the alert count and fire effects on new alerts
  useEffect(() => {
    if (alertCount == null) return;
    if (prevCountRef.current === null) {
      // First render — just store the count, don't notify
      prevCountRef.current = alertCount;
      return;
    }
    if (alertCount > prevCountRef.current) {
      const newCount = alertCount - prevCountRef.current;
      playAlertBlip();
      showPushNotification(
        `🚨 ${newCount} New Drishti Alert${newCount > 1 ? "s" : ""}`,
        "Tap to view the latest trading alert in your dashboard."
      );
    }
    prevCountRef.current = alertCount;
  }, [alertCount]);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return;
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, []);

  return { permission, requestPermission };
}

export { playAlertBlip, showPushNotification };
